/**
 * Collection Filters — Full AJAX Functionality
 * Handles: checkbox filters, price range, sort, reset, clear all,
 * pagination, add-to-cart, loading states, and URL sync.
 */
document.addEventListener('DOMContentLoaded', () => {
  const section = document.querySelector('.collection-filters[data-section-id]');
  if (!section) return;

  const sectionId = section.dataset.sectionId;
  const baseUrl = section.dataset.sectionUrl || window.location.pathname;

  /* ── Selectors ────────────────────────────────────────── */
  const sidebar   = section.querySelector('[data-cf-sidebar]');
  const backdrop  = section.querySelector('[data-cf-backdrop]');
  const form      = section.querySelector('[data-cf-form]');
  const gridWrap  = section.querySelector('[data-cf-grid-wrap]');

  let isFetching = false;

  /* ══════════════════════════════════════════════════════
     1. MOBILE SIDEBAR TOGGLE
     ══════════════════════════════════════════════════════ */
  function openSidebar() {
    if (!sidebar || !backdrop) return;
    sidebar.classList.add('is-open');
    backdrop.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }

  function closeSidebar() {
    if (!sidebar || !backdrop) return;
    sidebar.classList.remove('is-open');
    backdrop.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  section.querySelector('[data-cf-open]')?.addEventListener('click', openSidebar);
  section.querySelector('[data-cf-close]')?.addEventListener('click', closeSidebar);
  backdrop?.addEventListener('click', closeSidebar);

  /* ══════════════════════════════════════════════════════
     2. SERIALIZE FORM → URL SEARCH PARAMS
     ══════════════════════════════════════════════════════ */
  function buildFilterUrl() {
    if (!form) return baseUrl;

    const params = new URLSearchParams();

    // Checkboxes (list / boolean filters)
    form.querySelectorAll('input[type="checkbox"]:checked').forEach(cb => {
      params.append(cb.name, cb.value);
    });

    // Price range — values are now in dollars (converted from cents in Liquid)
    // Shopify URL params (filter.v.price.gte/lte) expect dollar values
    form.querySelectorAll('[data-cf-price]').forEach(wrap => {
      const minInput = wrap.querySelector('[data-cf-input-min]');
      const maxInput = wrap.querySelector('[data-cf-input-max]');
      const rangeMax = parseFloat(wrap.dataset.max) || 0;

      const minVal = parseFloat(minInput?.value);
      const maxVal = parseFloat(maxInput?.value);

      // Only include min if user moved it above 0
      if (minInput && !isNaN(minVal) && minVal > 0) {
        params.append(minInput.name, formatPriceValue(minInput.value));
      }
      // Only include max if user moved it below the range maximum
      if (maxInput && !isNaN(maxVal) && maxVal < rangeMax) {
        params.append(maxInput.name, formatPriceValue(maxInput.value));
      }
    });

    // Sort — re-query each time because form innerHTML swap detaches old element
    const currentSortInput = form.querySelector('[data-cf-sort-input]');
    if (currentSortInput && currentSortInput.value) {
      params.append('sort_by', currentSortInput.value);
    }

    const qs = params.toString();
    return qs ? `${baseUrl}?${qs}` : baseUrl;
  }

  /* ══════════════════════════════════════════════════════
     3. FETCH FILTERED HTML & SWAP CONTENT
     ══════════════════════════════════════════════════════ */
  async function fetchAndRender(url) {
    if (isFetching) return;
    isFetching = true;

    // Show loading state
    gridWrap?.classList.add('is-loading');

    // Append section_id for Shopify section rendering API
    const fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);

    try {
      const res = await fetch(fetchUrl.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector('.collection-filters[data-section-id]');

      if (!newSection) throw new Error('Section not found in response');

      // Swap: product grid + pagination
      const newGridWrap = newSection.querySelector('[data-cf-grid-wrap]');
      if (newGridWrap && gridWrap) {
        gridWrap.innerHTML = newGridWrap.innerHTML;
      }

      // Swap: toolbar (count text)
      const newCount = newSection.querySelector('[data-cf-count]');
      const oldCount = section.querySelector('[data-cf-count]');
      if (newCount && oldCount) {
        oldCount.textContent = newCount.textContent;
      }

      // Swap: filter sidebar form (to reflect updated counts + active states)
      const newForm = newSection.querySelector('[data-cf-form]');
      if (newForm && form) {
        form.innerHTML = newForm.innerHTML;
      }

      // Update sort select if present
      const newSort = newSection.querySelector('[data-cf-sort]');
      const oldSort = section.querySelector('[data-cf-sort]');
      if (newSort && oldSort) {
        oldSort.innerHTML = newSort.innerHTML;
      }

      // Update browser URL without reload
      const cleanUrl = new URL(url, window.location.origin);
      cleanUrl.searchParams.delete('section_id');
      history.replaceState(null, '', cleanUrl.toString());

      // Re-bind dynamic event listeners after DOM swap
      bindPriceSliders();
      bindResetGroups();
      bindClearAll();
      bindApplyButtons();
      bindCheckboxes();
      bindPagination();
      bindAddToCart();

    } catch (err) {
      console.error('[CollectionFilters] Fetch error:', err);
    } finally {
      isFetching = false;
      gridWrap?.classList.remove('is-loading');

      // Scroll to top of grid on filter change
      const toolbar = section.querySelector('.collection-filters__toolbar');
      if (toolbar) {
        toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  /* ══════════════════════════════════════════════════════
     4. CHECKBOX FILTER CHANGE
     ══════════════════════════════════════════════════════ */
  function bindCheckboxes() {
    if (!form) return;
    form.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.addEventListener('change', () => {
        fetchAndRender(buildFilterUrl());
      });
    });
  }

  /* ══════════════════════════════════════════════════════
     5. SORT DROPDOWN
     ══════════════════════════════════════════════════════ */
  function bindSort() {
    const sort = section.querySelector('[data-cf-sort]');
    if (!sort) return;
    sort.addEventListener('change', (e) => {
      // Update hidden sort input in form
      const hidden = section.querySelector('[data-cf-sort-input]');
      if (hidden) hidden.value = e.target.value;
      fetchAndRender(buildFilterUrl());
    });
  }

  /* ══════════════════════════════════════════════════════
     6. PRICE RANGE SLIDERS + INPUTS
     ══════════════════════════════════════════════════════ */
  const formatPriceValue = (value) => {
    const numericValue = Number.parseFloat(value);
    if (!Number.isFinite(numericValue)) return '';

    const roundedValue = Math.round(numericValue);
    return roundedValue < 0 ? '0' : String(roundedValue);
  };

  function bindPriceSliders() {
    section.querySelectorAll('[data-cf-price]').forEach(wrap => {
      const minSlider = wrap.querySelector('[data-cf-range-min]');
      const maxSlider = wrap.querySelector('[data-cf-range-max]');
      const minInput  = wrap.querySelector('[data-cf-input-min]');
      const maxInput  = wrap.querySelector('[data-cf-input-max]');
      const fill      = wrap.querySelector('[data-cf-price-fill]');
      const maxVal    = parseFloat(wrap.dataset.max) || 100;

      let priceApplyTimer = null;

      /* Format value to whole numbers (no decimals) */
      const toPrice = (v) => Math.round(parseFloat(v));

      /* Update the colored fill bar between the two thumbs */
      const updateFill = () => {
        if (!fill || !minSlider || !maxSlider) return;
        const lo = parseFloat(minSlider.value) || 0;
        const hi = parseFloat(maxSlider.value) || maxVal;
        const minP = maxVal > 0 ? (lo / maxVal) * 100 : 0;
        const maxP = maxVal > 0 ? (hi / maxVal) * 100 : 100;
        fill.style.left  = minP + '%';
        fill.style.right = (100 - maxP) + '%';
      };

      /* Debounced auto-apply: fires filter after slider is released */
      const scheduleApply = () => {
        if (priceApplyTimer) clearTimeout(priceApplyTimer);
        priceApplyTimer = setTimeout(() => {
          fetchAndRender(buildFilterUrl());
        }, 500);
      };

      if (!minSlider || !maxSlider) return;

      // Initial fill position
      updateFill();

      /* ── Z-index management ── */
      /* Bring the touched slider to front so its thumb is always grabbable */
      minSlider.addEventListener('pointerdown', () => {
        minSlider.style.zIndex = '4';
        maxSlider.style.zIndex = '3';
      });
      maxSlider.addEventListener('pointerdown', () => {
        maxSlider.style.zIndex = '4';
        minSlider.style.zIndex = '3';
      });

      /* ── Min slider events ── */
      minSlider.addEventListener('input', () => {
        let val = parseFloat(minSlider.value);
        const max = parseFloat(maxSlider.value);
        if (val > max) { val = max; minSlider.value = val; }
        if (minInput) minInput.value = toPrice(val);
        updateFill();
      });
      /* Auto-apply when user releases the slider thumb */
      minSlider.addEventListener('change', scheduleApply);

      /* ── Max slider events ── */
      maxSlider.addEventListener('input', () => {
        let val = parseFloat(maxSlider.value);
        const min = parseFloat(minSlider.value);
        if (val < min) { val = min; maxSlider.value = val; }
        if (maxInput) maxInput.value = toPrice(val);
        updateFill();
      });
      maxSlider.addEventListener('change', scheduleApply);

      /* ── Number input: From ── */
      if (minInput) {
        const syncMin = () => {
          let val = parseFloat(minInput.value);
          if (isNaN(val) || val < 0) val = 0;
          if (val > parseFloat(maxSlider.value)) val = parseFloat(maxSlider.value);
          minInput.value = toPrice(val);
          minSlider.value = val;
          updateFill();
        };
        minInput.addEventListener('change', syncMin);
        minInput.addEventListener('blur', () => { syncMin(); scheduleApply(); });
        minInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); syncMin(); scheduleApply(); }
        });
      }

      /* ── Number input: To ── */
      if (maxInput) {
        const syncMax = () => {
          let val = parseFloat(maxInput.value);
          if (isNaN(val) || val <= 0) val = maxVal;
          if (val > maxVal) val = maxVal;
          if (val < parseFloat(minSlider.value)) val = parseFloat(minSlider.value);
          maxInput.value = toPrice(val);
          maxSlider.value = val;
          updateFill();
        };
        maxInput.addEventListener('change', syncMax);
        maxInput.addEventListener('blur', () => { syncMax(); scheduleApply(); });
        maxInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); syncMax(); scheduleApply(); }
        });
      }
    });
  }

  /* ══════════════════════════════════════════════════════
     7. APPLY FILTER BUTTON (Price Range — manual fallback)
     ══════════════════════════════════════════════════════ */
  function bindApplyButtons() {
    section.querySelectorAll('[data-cf-apply]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        fetchAndRender(buildFilterUrl());
      });
    });
  }

  /* ══════════════════════════════════════════════════════
     8. RESET GROUP
     ══════════════════════════════════════════════════════ */
  function bindResetGroups() {
    section.querySelectorAll('[data-cf-reset-group]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const group = e.target.closest('[data-cf-group]');
        if (!group) return;

        // Uncheck all checkboxes in this group
        group.querySelectorAll('input[type="checkbox"]').forEach(cb => {
          cb.checked = false;
        });

        // Reset price sliders in this group
        const priceWrap = group.querySelector('[data-cf-price]');
        if (priceWrap) {
          const minSlider = priceWrap.querySelector('[data-cf-range-min]');
          const maxSlider = priceWrap.querySelector('[data-cf-range-max]');
          const minInput  = priceWrap.querySelector('[data-cf-input-min]');
          const maxInput  = priceWrap.querySelector('[data-cf-input-max]');
          if (minSlider) {
            minSlider.value = 0;
            if (minInput) minInput.value = 0;
          }
          if (maxSlider) {
            maxSlider.value = maxSlider.max;
            if (maxInput) maxInput.value = maxSlider.max;
          }
          // Update fill bar
          const fill = priceWrap.querySelector('[data-cf-price-fill]');
          if (fill) {
            fill.style.left = '0%';
            fill.style.right = '0%';
          }
        }

        // Re-fetch with updated params
        fetchAndRender(buildFilterUrl());
      });
    });
  }

  /* ══════════════════════════════════════════════════════
     9. CLEAR ALL FILTERS
     ══════════════════════════════════════════════════════ */
  function bindClearAll() {
    section.querySelectorAll('[data-cf-clear-all]').forEach(btn => {
      btn.addEventListener('click', () => {
        // Navigate to bare collection URL (with current sort preserved)
        const sort = section.querySelector('[data-cf-sort]');
        let url = baseUrl;
        if (sort && sort.value) {
          url += `?sort_by=${encodeURIComponent(sort.value)}`;
        }
        fetchAndRender(url);
      });
    });
  }

  /* ══════════════════════════════════════════════════════
     10. PAGINATION — AJAX INTERCEPT
     ══════════════════════════════════════════════════════ */
  function bindPagination() {
    section.querySelectorAll('[data-cf-page-link]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const href = link.getAttribute('href');
        if (!href || href === '#') return;

        // Build full URL preserving current filter params
        const linkUrl = new URL(href, window.location.origin);

        // Get page param from the pagination link
        const page = linkUrl.searchParams.get('page');

        // Build current filter URL and append page
        const filterUrl = new URL(buildFilterUrl(), window.location.origin);
        if (page) {
          filterUrl.searchParams.set('page', page);
        }

        fetchAndRender(filterUrl.toString());
      });
    });
  }

  /* ══════════════════════════════════════════════════════
     11. ADD TO CART — AJAX (Dawn-native sections approach)
     ══════════════════════════════════════════════════════ */
  function bindAddToCart() {
    section.querySelectorAll('[data-cf-cart-form]').forEach(cartForm => {
      cartForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = cartForm.querySelector('.collection-filters__add-btn');
        const label = cartForm.querySelector('[data-cf-add-label]');
        if (!btn || btn.disabled) return;

        const originalText = label ? label.textContent.trim() : 'Add to cart';

        // Loading state
        btn.disabled = true;
        if (label) label.textContent = 'Adding...';

        try {
          const formData = new FormData(cartForm);
          const variantId = parseInt(formData.get('id'));
          if (!variantId) throw new Error('Missing variant id');

          // Ask Shopify to return updated section HTML for the cart drawer + bubble
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname
            })
          });

          if (!res.ok) {
            const err = await res.json();
            throw new Error(err.description || 'Could not add to cart');
          }

          // parsedState.sections contains the updated HTML for each section key
          const parsedState = await res.json();

          // ── Update cart count bubble ──────────────────────────────
          const bubbleHtml = parsedState.sections?.['cart-icon-bubble'];
          if (bubbleHtml) {
            const tempBubble = document.createElement('div');
            tempBubble.innerHTML = bubbleHtml;
            const newBubble = tempBubble.querySelector('#cart-icon-bubble');
            const currentBubble = document.getElementById('cart-icon-bubble');
            if (newBubble && currentBubble) {
              currentBubble.innerHTML = newBubble.innerHTML;
            }
          }

          // ── Open cart drawer (Dawn web component or Custom) ────────
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            if (typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(parsedState);
            } else {
              // Manual fallback for themes without renderContents
              const drawerHtml = parsedState.sections?.['cart-drawer'];
              if (drawerHtml) {
                const tempDrawer = document.createElement('div');
                tempDrawer.innerHTML = drawerHtml;
                const newDrawer = tempDrawer.querySelector('cart-drawer') || tempDrawer.firstElementChild;
                if (newDrawer) {
                  cartDrawer.innerHTML = newDrawer.innerHTML;
                }
              }

              // Force open classes
              cartDrawer.classList.add('active', 'animate', 'is-open');
              cartDrawer.classList.remove('is-empty');
              
              const innerDrawer = cartDrawer.querySelector('.drawer');
              if (innerDrawer) {
                innerDrawer.classList.add('active', 'animate', 'is-open');
              }
              document.body.classList.add('overflow-hidden');
            }
          } else {
            // ── Fallback: cart-notification popup ────────────────────
            const cartNotification = document.querySelector('cart-notification');
            if (cartNotification && typeof cartNotification.renderContents === 'function') {
              cartNotification.renderContents(parsedState);
            } else if (cartNotification && typeof cartNotification.open === 'function') {
              cartNotification.open();
            } else {
              const cartIcon = document.getElementById('cart-icon-bubble');
              if (cartIcon) {
                cartIcon.click();
              } else {
                window.location.href = window.routes?.cart_url || '/cart';
              }
            }
          }

          // Notify other theme scripts
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: parsedState }));

          // Success feedback on button
          if (label) label.textContent = 'Added ✓';
          btn.classList.add('is-added');

          setTimeout(() => {
            if (label) label.textContent = originalText;
            btn.disabled = false;
            btn.classList.remove('is-added');
          }, 2000);

        } catch (err) {
          console.error('[CollectionFilters] Add to cart error:', err);
          if (label) label.textContent = 'Error — try again';
          setTimeout(() => {
            if (label) label.textContent = originalText;
            btn.disabled = false;
          }, 2500);
        }
      });
    });
  }

  /* ══════════════════════════════════════════════════════
     INIT — Bind all event listeners
     ══════════════════════════════════════════════════════ */
  bindCheckboxes();
  bindSort();
  bindPriceSliders();
  bindApplyButtons();
  bindResetGroups();
  bindClearAll();
  bindPagination();
  bindAddToCart();
});
